import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader, CardTitle, Col, Container, Row } from "reactstrap";
import MapWithMarkers from "../map";
import { TDevice } from "../../../../common/types";
import { TNullable } from "../../../../common/types/common";
import { useAllFusionDevices } from "../../../../common/hooks/api/device";
import { getResponseItemData } from "../../../../helpers/response-helper";
import { toastError } from "../../../../helpers/toast-helper";

const DevicesMap = () => {
    const [selectedDevice, setSelectedDevice] = useState<TNullable<TDevice>>(null);

    const { data: devicesResponse, isLoading, error } = useAllFusionDevices({
        onError: (err) => {
            console.error("Error fetching Fusion devices:", err);
            toastError("Cannot fetch Fusion devices");
        }
    });

    const { data } = getResponseItemData(devicesResponse);

    // Devices are already filtered to Fusion = 1 from the backend
    const fusionDevices = useMemo(() => {
        if (!data) return [];
        // Ensure data is an array
        return Array.isArray(data) ? data : [];
    }, [data]);

    // Filter devices that have location data
    const devicesWithCoords = useMemo(() => {
        if (!fusionDevices) return [];
        return fusionDevices.filter((device): device is TDevice => 
            'lastKnownLocation' in device && !!(device as TDevice).lastKnownLocation
        );
    }, [fusionDevices]);

    // Debug logging (remove after fixing)
    useEffect(() => {
        if (devicesResponse) {
            console.log("Devices response:", devicesResponse);
            console.log("Extracted data:", data);
            console.log("Data type:", Array.isArray(data) ? "array" : typeof data);
            console.log("Data length:", Array.isArray(data) ? data.length : "not an array");
            console.log("Fusion devices count:", fusionDevices.length);
            console.log("Devices with coords count:", devicesWithCoords.length);
        }
        if (error) {
            console.error("Error details:", error);
        }
    }, [devicesResponse, data, fusionDevices, devicesWithCoords, error]);

    const deviceList = useMemo(() => {
        if (!fusionDevices) return [];
        return fusionDevices.map((device) => {
            const deviceWithLocation = device as TDevice;
            const hasLocation = 'lastKnownLocation' in device && deviceWithLocation.lastKnownLocation;
            return (
                <div key={`device-row-${device.serialNumber}`} className="d-flex justify-content-between mb-2">
                    {hasLocation
                        ? (
                            <a
                                className="fs-16 fw-bold"
                                onClick={() => { setSelectedDevice(deviceWithLocation); }}
                                title="Show device on the map"
                                style={{ cursor: 'pointer' }}
                            >{device.serialNumber}</a>
                        ) : device.serialNumber
                    }

                    <Link
                        to={`/machines/${device.serialNumber}`}
                        className="fs-16 fw-bold"
                    >
                        <i className="fa fa-arrow-right" aria-hidden="true"></i>
                    </Link>
                </div>
            );
        });
    }, [fusionDevices]);

    return (
        <Container fluid className="m-0 p-0 mt-3">
            <Row>
                <Col xl={9} lg={12} className="mb-3">
                    <Card className="h-100">
                        <CardHeader>
                            <CardTitle>All Fusion Units - Last Known Locations</CardTitle>
                        </CardHeader>
                        <CardBody className="pt-0 pb-0">
                            <div style={{height: '500px'}}>
                                {isLoading ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{height: '100%'}}>
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <MapWithMarkers
                                        devices={devicesWithCoords}
                                        selectedDevice={selectedDevice}
                                    />
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col xl={3} lg={12} className="mb-3">
                    <Card className="h-100">
                        <CardHeader>
                            <CardTitle>All Fusion Machines ({fusionDevices?.length || 0})</CardTitle>
                        </CardHeader>
                        <CardBody>
                            {deviceList?.length ? deviceList : "No machines yet"}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default DevicesMap;

